import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  Plus, 
  Save, 
  Trash2, 
  Search, 
  X,
  Database,
  Building,
  MapPin,
  DollarSign,
  Users,
  GraduationCap,
  Download,
  Upload,
  Edit3,
  Check,
  AlertCircle,
  Link as LinkIcon,
  Globe,
  Star,
  Clock,
  BookOpen
} from 'lucide-react';
import { collegeFAQService } from '../lib/database';
import type { CollegeFAQ } from '../lib/supabase';
import { collegeService } from '../lib/database';
import type { College } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const CollegeManager = () => {
  const { user, isAdmin } = useAuth();
  const [colleges, setColleges] = useState<College[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFAQManager, setShowFAQManager] = useState<string | null>(null);
  const [collegeFAQs, setCollegeFAQs] = useState<CollegeFAQ[]>([]);
  const [newFAQ, setNewFAQ] = useState({
    question: '',
    answer: '',
    order_index: 0,
    is_active: true
  });
  const [editingFAQId, setEditingFAQId] = useState<string | null>(null);

  const [newCollege, setNewCollege] = useState({
    name: '',
    slug: '',
    description: '',
    location: '',
    state: '',
    category: 'Engineering',
    stream: 'Engineering',
    rating: 4.0,
    fees: '',
    cutoff: '',
    established: '',
    type: 'Government',
    image_url: 'https://images.pexels.com/photos/1454360/pexels-photo-1454360.jpeg?auto=compress&cs=tinysrgb&w=800',
    contact_phone: '',
    contact_email: '',
    contact_website: '',
    courses: [] as string[],
    specializations: [] as string[],
    facilities: [] as string[],
    // SEO Fields
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    focus_keyword: '',
    canonical_url: '',
    og_title: '',
    og_description: '',
    og_image: '',
    twitter_title: '',
    twitter_description: '',
    schema_type: 'EducationalOrganization'
  });

  const categories = ['Engineering', 'Medical', 'Law', 'Management', 'Arts', 'Commerce', 'Science'];
  const states = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 
    'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 
    'Uttarakhand', 'West Bengal', 'Andaman and Nicobar Islands', 'Chandigarh', 
    'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir', 
    'Ladakh', 'Lakshadweep', 'Puducherry'
  ];
  const types = ['Government', 'Private'];

  const defaultCourses = {
    Engineering: ['B.Tech Computer Science', 'B.Tech Electronics', 'B.Tech Mechanical', 'B.Tech Civil', 'B.Tech Electrical'],
    Medical: ['MBBS', 'BDS', 'BAMS', 'BHMS', 'B.Pharma'],
    Management: ['MBA', 'BBA', 'PGDM', 'Executive MBA'],
    Law: ['BA LLB', 'BBA LLB', 'LLM', 'B.Com LLB'],
    Arts: ['BA', 'MA', 'B.Fine Arts', 'M.Fine Arts'],
    Commerce: ['B.Com', 'M.Com', 'BBA', 'MBA'],
    Science: ['BSc', 'MSc', 'B.Tech', 'M.Tech']
  };

  const defaultSpecializations = {
    Engineering: ['Computer Science', 'Electronics & Communication', 'Mechanical', 'Civil', 'Electrical'],
    Medical: ['General Medicine', 'Surgery', 'Pediatrics', 'Cardiology', 'Dermatology'],
    Management: ['Finance', 'Marketing', 'Operations', 'HR', 'Strategy'],
    Law: ['Corporate Law', 'Criminal Law', 'Constitutional Law', 'International Law'],
    Arts: ['Literature', 'History', 'Philosophy', 'Psychology', 'Sociology'],
    Commerce: ['Accounting', 'Finance', 'Economics', 'Business Studies'],
    Science: ['Physics', 'Chemistry', 'Mathematics', 'Biology', 'Computer Science']
  };

  const defaultFacilities = [
    'Central Library', 'Computer Labs', 'Research Centers', 'Sports Complex', 
    'Hostels', 'Medical Center', 'Cafeteria', 'Auditorium', 'Workshop', 'Gymnasium'
  ];

  useEffect(() => {
    if (isAdmin) {
      fetchColleges();
    }
  }, [isAdmin, searchQuery]);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      const data = await collegeService.getAll({ search: searchQuery });
      setColleges(data);
    } catch (error) {
      console.error('Error fetching colleges:', error);
      toast.error('Failed to fetch colleges');
    } finally {
      setLoading(false);
    }
  };

  // Generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Auto-generate SEO fields
  const autoGenerateSEO = (name: string, description: string, category: string, location: string, state: string) => {
    const slug = generateSlug(name);
    const currentYear = new Date().getFullYear();
    
    return {
      meta_title: name,
      meta_description: description,
      meta_keywords: `${name}, ${name} reviews, ${name} admission, ${name} fees, ${name} cutoff, ${category} college ${location}, ${category} colleges in ${state}, ${location} colleges, ${state} colleges, college fees, admission cutoff, student reviews, college comparison, grad vikalp`,
      focus_keyword: `${name} college`,
      canonical_url: `/college/${slug}`,
      og_title: name,
      og_description: description,
      twitter_title: name,
      twitter_description: description
    };
  };

  const handleInputChange = (field: string, value: any) => {
    setNewCollege(prev => {
      const updatedState = {
        ...prev,
        [field]: value
      };

      // Auto-generate SEO fields when name, description, category, location, or state changes
      if (field === 'name' || field === 'description' || field === 'category' || field === 'location' || field === 'state') {
        const name = field === 'name' ? value : prev.name;
        const description = field === 'description' ? value : prev.description;
        const category = field === 'category' ? value : prev.category;
        const location = field === 'location' ? value : prev.location;
        const state = field === 'state' ? value : prev.state;
        
        if (name && location && state) {
          const seoFields = autoGenerateSEO(name, description, category, location, state);
          return {
            ...updatedState,
            ...seoFields,
            og_image: updatedState.image_url
          };
        }
      }

      // Auto-populate courses and specializations based on category
      if (field === 'category') {
        return {
          ...updatedState,
          stream: value,
          courses: defaultCourses[value as keyof typeof defaultCourses] || [],
          specializations: defaultSpecializations[value as keyof typeof defaultSpecializations] || []
        };
      }

      return updatedState;
    });
  };

  const handleArrayChange = (field: 'courses' | 'specializations' | 'facilities', value: string) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
    setNewCollege(prev => ({
      ...prev,
      [field]: items
    }));
  };

  const resetForm = () => {
    setNewCollege({
      name: '',
      slug: '',
      description: '',
      location: '',
      state: '',
      category: 'Engineering',
      stream: 'Engineering',
      rating: 4.0,
      fees: '',
      cutoff: '',
      established: '',
      type: 'Government',
      image_url: 'https://images.pexels.com/photos/1454360/pexels-photo-1454360.jpeg?auto=compress&cs=tinysrgb&w=800',
      contact_phone: '',
      contact_email: '',
      contact_website: '',
      courses: defaultCourses.Engineering,
      specializations: defaultSpecializations.Engineering,
      facilities: defaultFacilities,
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      focus_keyword: '',
      canonical_url: '',
      og_title: '',
      og_description: '',
      og_image: '',
      twitter_title: '',
      twitter_description: '',
      schema_type: 'EducationalOrganization'
    });
  };

  const validateForm = () => {
    if (!newCollege.name.trim()) {
      toast.error('College name is required');
      return false;
    }
    if (!newCollege.slug.trim()) {
      toast.error('URL slug is required');
      return false;
    }
    if (!newCollege.location.trim()) {
      toast.error('Location is required');
      return false;
    }
    if (!newCollege.state) {
      toast.error('State is required');
      return false;
    }
    if (!newCollege.fees.trim()) {
      toast.error('Fees information is required');
      return false;
    }
    if (!newCollege.cutoff.trim()) {
      toast.error('Cutoff information is required');
      return false;
    }
    if (!newCollege.established.trim()) {
      toast.error('Establishment year is required');
      return false;
    }
    if (!newCollege.meta_title.trim()) {
      toast.error('Meta title is required for SEO');
      return false;
    }
    if (!newCollege.meta_description.trim()) {
      toast.error('Meta description is required for SEO');
      return false;
    }
    return true;
  };

  const saveCollege = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (editingId) {
        await collegeService.update(editingId, newCollege);
        toast.success('College updated successfully!');
        setEditingId(null);
      } else {
        await collegeService.create(newCollege);
        toast.success('College created successfully!');
        setShowAddForm(false);
      }
      
      resetForm();
      fetchColleges();
    } catch (error) {
      console.error('Error saving college:', error);
      toast.error('Failed to save college. Please check your permissions.');
    } finally {
      setSaving(false);
    }
  };

  const editCollege = (college: College) => {
    setNewCollege({
      name: college.name,
      slug: college.slug,
      description: college.description,
      location: college.location,
      state: college.state,
      category: college.category,
      stream: college.stream,
      rating: college.rating,
      fees: college.fees,
      cutoff: college.cutoff,
      established: college.established,
      type: college.type,
      image_url: college.image_url,
      contact_phone: college.contact_phone || '',
      contact_email: college.contact_email || '',
      contact_website: college.contact_website || '',
      courses: college.courses || [],
      specializations: college.specializations || [],
      facilities: college.facilities || [],
      meta_title: college.meta_title || college.name,
      meta_description: college.meta_description || college.description,
      meta_keywords: college.meta_keywords || '',
      focus_keyword: college.focus_keyword || '',
      canonical_url: college.canonical_url || `/college/${college.slug}`,
      og_title: college.og_title || college.name,
      og_description: college.og_description || college.description,
      og_image: college.og_image || college.image_url,
      twitter_title: college.twitter_title || college.name,
      twitter_description: college.twitter_description || college.description,
      schema_type: college.schema_type || 'EducationalOrganization'
    });
    setEditingId(college.id);
    setShowAddForm(true);
  };

  const deleteCollege = async (college: College) => {
    if (!confirm(`Are you sure you want to delete "${college.name}"?`)) return;

    try {
      await collegeService.delete(college.id);
      setColleges(prev => prev.filter(c => c.id !== college.id));
      toast.success('College deleted successfully!');
    } catch (error) {
      console.error('Error deleting college:', error);
      toast.error('Failed to delete college');
    }
  };

  const fetchCollegeFAQs = async (collegeId: string) => {
    try {
      const data = await collegeFAQService.getByCollegeId(collegeId);
      setCollegeFAQs(data);
    } catch (error) {
      console.error('Error fetching college FAQs:', error);
      toast.error('Failed to fetch FAQs');
    }
  };

  const saveFAQ = async () => {
    if (!newFAQ.question.trim() || !newFAQ.answer.trim() || !showFAQManager) return;

    try {
      if (editingFAQId) {
        await collegeFAQService.update(editingFAQId, newFAQ);
        toast.success('FAQ updated successfully!');
        setEditingFAQId(null);
      } else {
        await collegeFAQService.create({
          ...newFAQ,
          college_id: showFAQManager
        });
        toast.success('FAQ added successfully!');
      }
      
      setNewFAQ({ question: '', answer: '', order_index: 0, is_active: true });
      fetchCollegeFAQs(showFAQManager);
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('Failed to save FAQ');
    }
  };

  const editFAQ = (faq: CollegeFAQ) => {
    setNewFAQ({
      question: faq.question,
      answer: faq.answer,
      order_index: faq.order_index,
      is_active: faq.is_active
    });
    setEditingFAQId(faq.id);
  };

  const deleteFAQ = async (faqId: string) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      await collegeFAQService.delete(faqId);
      toast.success('FAQ deleted successfully!');
      if (showFAQManager) {
        fetchCollegeFAQs(showFAQManager);
      }
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Failed to delete FAQ');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setShowAddForm(false);
    resetForm();
  };

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <AlertCircle className="h-16 w-16 text-warning mx-auto mb-3" />
                </div>
                <h1 className="h2 text-dark mb-3">Access Restricted</h1>
                <p className="text-muted mb-4">
                  You need admin privileges to access the College Management system.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <Link to="/" className="btn btn-primary">
                    Back to Home
                  </Link>
                  <Link to="/contact" className="btn btn-outline-primary">
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading college management...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
      <div className="container py-4">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-primary rounded-3 p-3">
              <Building className="text-white" size={32} />
            </div>
            <div>
              <h1 className="h2 fw-bold text-dark mb-1">College Manager</h1>
              <p className="text-muted mb-0">Manage colleges with SEO optimization</p>
            </div>
          </div>
          <Link to="/admin" className="btn btn-outline-secondary">
            Back to Admin
          </Link>
        </div>

        {/* Action Bar */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <div className="d-flex gap-2">
            <button
              onClick={() => {
                resetForm();
                setShowAddForm(true);
                setEditingId(null);
              }}
              className="btn btn-success d-flex align-items-center gap-2"
            >
              <Plus size={16} />
              Add New College
            </button>
            <span className="badge bg-primary fs-6 px-3 py-2">
              {colleges.length} Colleges
            </span>
          </div>

          <div className="position-relative">
            <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={16} />
            <input
              type="text"
              placeholder="Search colleges..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-control ps-5"
              style={{ width: '300px' }}
            />
          </div>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card border-0 shadow-lg mb-4"
          >
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">{editingId ? 'Edit College' : 'Add New College'}</h5>
            </div>
            <div className="card-body p-4">
              {/* Basic Information */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3">🏛️ Basic Information</h6>
                <div className="row g-3">
                  <div className="col-md-8">
                    <label className="form-label fw-semibold">College Name *</label>
                    <input
                      type="text"
                      value={newCollege.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="form-control"
                      placeholder="Enter college name"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Rating</label>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      step="0.1"
                      value={newCollege.rating}
                      onChange={(e) => handleInputChange('rating', parseFloat(e.target.value))}
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      URL Slug * 
                      <small className="text-muted">(SEO-friendly URL)</small>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <LinkIcon size={16} />
                      </span>
                      <input
                        type="text"
                        value={newCollege.slug}
                        onChange={(e) => handleInputChange('slug', e.target.value)}
                        className="form-control"
                        placeholder="college-name-slug"
                      />
                    </div>
                    <div className="form-text">
                      Preview: /college/{newCollege.slug || 'your-slug-here'}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Category *</label>
                    <select
                      value={newCollege.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="form-select"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Location *</label>
                    <input
                      type="text"
                      value={newCollege.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className="form-control"
                      placeholder="City name"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">State *</label>
                    <select
                      value={newCollege.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select State</option>
                      {states.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Type *</label>
                    <select
                      value={newCollege.type}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                      className="form-select"
                    >
                      {types.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Established *</label>
                    <input
                      type="text"
                      value={newCollege.established}
                      onChange={(e) => handleInputChange('established', e.target.value)}
                      className="form-control"
                      placeholder="e.g., 1961"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Fees *</label>
                    <input
                      type="text"
                      value={newCollege.fees}
                      onChange={(e) => handleInputChange('fees', e.target.value)}
                      className="form-control"
                      placeholder="e.g., ₹2.5L/year"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Cutoff Information *</label>
                    <input
                      type="text"
                      value={newCollege.cutoff}
                      onChange={(e) => handleInputChange('cutoff', e.target.value)}
                      className="form-control"
                      placeholder="e.g., JEE Advanced Rank 1-500"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Description</label>
                    <textarea
                      value={newCollege.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="form-control"
                      rows={3}
                      placeholder="Brief description of the college..."
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3">📞 Contact Information</h6>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Contact Phone</label>
                    <input
                      type="text"
                      value={newCollege.contact_phone}
                      onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                      className="form-control"
                      placeholder="+91-11-2659-1000"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Contact Email</label>
                    <input
                      type="email"
                      value={newCollege.contact_email}
                      onChange={(e) => handleInputChange('contact_email', e.target.value)}
                      className="form-control"
                      placeholder="info@college.edu"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Website</label>
                    <input
                      type="url"
                      value={newCollege.contact_website}
                      onChange={(e) => handleInputChange('contact_website', e.target.value)}
                      className="form-control"
                      placeholder="https://college.edu"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Image URL</label>
                    <input
                      type="url"
                      value={newCollege.image_url}
                      onChange={(e) => handleInputChange('image_url', e.target.value)}
                      className="form-control"
                      placeholder="https://images.pexels.com/..."
                    />
                  </div>
                </div>
              </div>

              {/* Academic Information */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3">🎓 Academic Information</h6>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Courses (comma-separated)</label>
                    <textarea
                      value={newCollege.courses.join(', ')}
                      onChange={(e) => handleArrayChange('courses', e.target.value)}
                      className="form-control"
                      rows={3}
                      placeholder="B.Tech, M.Tech, PhD"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Specializations (comma-separated)</label>
                    <textarea
                      value={newCollege.specializations.join(', ')}
                      onChange={(e) => handleArrayChange('specializations', e.target.value)}
                      className="form-control"
                      rows={3}
                      placeholder="Computer Science, Electronics"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Facilities (comma-separated)</label>
                    <textarea
                      value={newCollege.facilities.join(', ')}
                      onChange={(e) => handleArrayChange('facilities', e.target.value)}
                      className="form-control"
                      rows={3}
                      placeholder="Library, Labs, Hostels"
                    />
                  </div>
                </div>
              </div>

              {/* SEO Optimization */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3">🔍 SEO Optimization</h6>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label fw-semibold">Meta Title *</label>
                    <input
                      type="text"
                      value={newCollege.meta_title}
                      onChange={(e) => handleInputChange('meta_title', e.target.value)}
                      className="form-control"
                      placeholder="SEO-optimized title for search engines"
                    />
                    <div className="form-text">
                      Recommended: 50-60 characters. Current: {newCollege.meta_title?.length || 0}
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Meta Description *</label>
                    <textarea
                      value={newCollege.meta_description}
                      onChange={(e) => handleInputChange('meta_description', e.target.value)}
                      className="form-control"
                      rows={3}
                      placeholder="Description that appears in search results"
                    />
                    <div className="form-text">
                      Recommended: 150-160 characters. Current: {newCollege.meta_description?.length || 0}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Focus Keyword</label>
                    <input
                      type="text"
                      value={newCollege.focus_keyword}
                      onChange={(e) => handleInputChange('focus_keyword', e.target.value)}
                      className="form-control"
                      placeholder="Primary keyword to rank for"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Canonical URL</label>
                    <input
                      type="text"
                      value={newCollege.canonical_url}
                      onChange={(e) => handleInputChange('canonical_url', e.target.value)}
                      className="form-control"
                      placeholder="/college/your-college-slug"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Meta Keywords</label>
                    <textarea
                      value={newCollege.meta_keywords}
                      onChange={(e) => handleInputChange('meta_keywords', e.target.value)}
                      className="form-control"
                      rows={2}
                      placeholder="SEO keywords separated by commas"
                    />
                  </div>
                </div>
              </div>

              {/* Social Media Optimization */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3">📱 Social Media Optimization</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Open Graph Title</label>
                    <input
                      type="text"
                      value={newCollege.og_title}
                      onChange={(e) => handleInputChange('og_title', e.target.value)}
                      className="form-control"
                      placeholder="Title for Facebook/LinkedIn sharing"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Twitter Title</label>
                    <input
                      type="text"
                      value={newCollege.twitter_title}
                      onChange={(e) => handleInputChange('twitter_title', e.target.value)}
                      className="form-control"
                      placeholder="Title for Twitter sharing"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Open Graph Description</label>
                    <textarea
                      value={newCollege.og_description}
                      onChange={(e) => handleInputChange('og_description', e.target.value)}
                      className="form-control"
                      rows={2}
                      placeholder="Description for Facebook/LinkedIn"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Twitter Description</label>
                    <textarea
                      value={newCollege.twitter_description}
                      onChange={(e) => handleInputChange('twitter_description', e.target.value)}
                      className="form-control"
                      rows={2}
                      placeholder="Description for Twitter"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Social Image URL</label>
                    <input
                      type="url"
                      value={newCollege.og_image}
                      onChange={(e) => handleInputChange('og_image', e.target.value)}
                      className="form-control"
                      placeholder="https://images.pexels.com/... (1200x630px recommended)"
                    />
                  </div>
                </div>
              </div>

              {/* SEO Preview */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3">👁️ SEO Preview</h6>
                <div className="card border">
                  <div className="card-body p-3">
                    <div className="mb-3">
                      <h6 className="small fw-semibold text-muted mb-2">Google Search Result Preview:</h6>
                      <div className="border p-3 rounded bg-white">
                        <div style={{ fontFamily: 'Arial, sans-serif' }}>
                          <div style={{ color: '#1a0dab', fontSize: '18px', marginBottom: '4px' }}>
                            {newCollege.meta_title || newCollege.name || 'College Name'}
                          </div>
                          <div style={{ color: '#006621', fontSize: '14px', marginBottom: '4px' }}>
                            https://gradvikalp.com{newCollege.canonical_url || `/college/${newCollege.slug || 'college-slug'}`}
                          </div>
                          <div style={{ color: '#545454', fontSize: '14px', lineHeight: '1.4' }}>
                            {newCollege.meta_description || newCollege.description || 'Meta description will appear here...'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="d-flex gap-2 mt-4">
                <button
                  onClick={saveCollege}
                  disabled={saving}
                  className="btn btn-primary d-flex align-items-center gap-2"
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {editingId ? 'Update College' : 'Create College'}
                    </>
                  )}
                </button>
                <button
                  onClick={cancelEdit}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* College List */}
        <div className="row g-4">
          {colleges.map((college) => (
            <div key={college.id} className="col-12">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card border-0 shadow-sm"
              >
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <img
                        src={college.image_url}
                        alt={college.name}
                        className="img-fluid rounded"
                        style={{ height: '120px', width: '100%', objectFit: 'cover' }}
                      />
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex gap-2 mb-2">
                        <span className="badge bg-primary">{college.category}</span>
                        <span className="badge bg-success">{college.type}</span>
                        <span className="badge bg-info">Rating: {college.rating}/5</span>
                      </div>
                      <h5 className="fw-bold text-dark mb-2">{college.name}</h5>
                      <p className="text-muted small mb-2 line-clamp-2">{college.description}</p>
                      <div className="vstack gap-1 small text-muted">
                        <div className="d-flex align-items-center gap-2">
                          <MapPin size={14} />
                          <span>{college.location}, {college.state}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <DollarSign size={14} />
                          <span>{college.fees}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Users size={14} />
                          <span>Established: {college.established}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Globe size={14} />
                          <span className="text-primary">/college/{college.slug}</span>
                        </div>
                      </div>
                      {college.courses && college.courses.length > 0 && (
                        <div className="mt-2">
                          {college.courses.slice(0, 3).map((course, idx) => (
                            <span key={idx} className="badge bg-light text-dark me-1 small">
                              {course}
                            </span>
                          ))}
                          {college.courses.length > 3 && (
                            <span className="badge bg-light text-dark small">
                              +{college.courses.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="col-md-3 text-end">
                      <div className="d-flex flex-column gap-2">
                        <Link
                          to={`/college/${college.slug}`}
                          target="_blank"
                          className="btn btn-outline-info btn-sm d-flex align-items-center gap-2"
                        >
                          <LinkIcon size={14} />
                          View Page
                        </Link>
                        <button
                          onClick={() => editCollege(college)}
                          className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
                        >
                          <Edit3 size={14} />
                          Edit
                        </button>
                        <button
                          onClick={() => deleteCollege(college)}
                          className="btn btn-outline-danger btn-sm d-flex align-items-center gap-2"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                        <button
                          onClick={() => {
                            setShowFAQManager(college.id);
                            fetchCollegeFAQs(college.id);
                          }}
                          className="btn btn-outline-info btn-sm d-flex align-items-center gap-2"
                        >
                          <BookOpen size={14} />
                          Manage FAQs
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </div>

        {colleges.length === 0 && !loading && (
          <div className="text-center py-5">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <Building className="text-primary mx-auto mb-3" size={64} />
                </div>
                <h3 className="h4 text-dark mb-3">No colleges found</h3>
                <p className="text-muted mb-4">
                  {searchQuery 
                    ? 'No colleges match your search criteria. Try adjusting your search terms.'
                    : 'Get started by creating your first SEO-optimized college page.'
                  }
                </p>
                <button
                  onClick={() => {
                    resetForm();
                    setShowAddForm(true);
                    setEditingId(null);
                  }}
                  className="btn btn-primary"
                >
                  Create Your First College
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* FAQ Manager Modal */}
      {showFAQManager && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">Manage College FAQs</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowFAQManager(null);
                    setEditingFAQId(null);
                    setNewFAQ({ question: '', answer: '', order_index: 0, is_active: true });
                  }}
                ></button>
              </div>
              <div className="modal-body p-4">
                {/* Add FAQ Form */}
                <div className="card border mb-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">{editingFAQId ? 'Edit FAQ' : 'Add New FAQ'}</h6>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label fw-semibold">Question *</label>
                        <input
                          type="text"
                          value={newFAQ.question}
                          onChange={(e) => setNewFAQ(prev => ({ ...prev, question: e.target.value }))}
                          className="form-control"
                          placeholder="Enter FAQ question"
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">Answer *</label>
                        <textarea
                          value={newFAQ.answer}
                          onChange={(e) => setNewFAQ(prev => ({ ...prev, answer: e.target.value }))}
                          className="form-control"
                          rows={4}
                          placeholder="Enter FAQ answer"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Order Index</label>
                        <input
                          type="number"
                          value={newFAQ.order_index}
                          onChange={(e) => setNewFAQ(prev => ({ ...prev, order_index: parseInt(e.target.value) }))}
                          className="form-control"
                          min="0"
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="form-check mt-4">
                          <input
                            type="checkbox"
                            id="college-faq-active"
                            checked={newFAQ.is_active}
                            onChange={(e) => setNewFAQ(prev => ({ ...prev, is_active: e.target.checked }))}
                            className="form-check-input"
                          />
                          <label className="form-check-label fw-semibold" htmlFor="college-faq-active">
                            Active
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <div className="d-flex gap-2 mt-4">
                      <button
                        onClick={saveFAQ}
                        className="btn btn-primary d-flex align-items-center gap-2"
                      >
                        <Save size={16} />
                        {editingFAQId ? 'Update FAQ' : 'Add FAQ'}
                      </button>
                      {editingFAQId && (
                        <button
                          onClick={() => {
                            setEditingFAQId(null);
                            setNewFAQ({ question: '', answer: '', order_index: 0, is_active: true });
                          }}
                          className="btn btn-secondary"
                        >
                          Cancel Edit
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* FAQ List */}
                <div className="card border">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">Existing FAQs ({collegeFAQs.length})</h6>
                  </div>
                  <div className="card-body">
                    {collegeFAQs.length > 0 ? (
                      <div className="vstack gap-3">
                        {collegeFAQs.map((faq, index) => (
                          <div key={faq.id} className="card border">
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="flex-grow-1">
                                  <h6 className="fw-bold text-dark mb-1">
                                    {index + 1}. {faq.question}
                                  </h6>
                                  <p className="text-muted mb-2">{faq.answer}</p>
                                  <div className="d-flex gap-2">
                                    <span className={`badge ${faq.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                      {faq.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                    <span className="badge bg-light text-dark">
                                      Order: {faq.order_index}
                                    </span>
                                  </div>
                                </div>
                                <div className="d-flex gap-2">
                                  <button
                                    onClick={() => editFAQ(faq)}
                                    className="btn btn-sm btn-outline-primary"
                                  >
                                    <Edit3 size={14} />
                                  </button>
                                  <button
                                    onClick={() => deleteFAQ(faq.id)}
                                    className="btn btn-sm btn-outline-danger"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-muted">No FAQs added yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollegeManager;